
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStatus } from './subscription/useSubscriptionStatus';
import { useSubscriptionCreation } from './subscription/useSubscriptionCreation';

export const useSubscription = () => {
  const navigate = useNavigate();
  const subscribingRef = useRef(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const initialMountRef = useRef(true);
  
  // Setup subscription status hooks
  const { 
    isSubscribed, 
    currentSubscription, 
    refreshSubscription,
    isLoading: statusLoading,
    cleanupSubscriptionStatus
  } = useSubscriptionStatus();
  
  // Setup subscription creation hooks
  const { 
    isLoading: creationLoading, 
    error, 
    paypalError, 
    hasCredentialError, 
    handleSubscribe: _handleSubscribe,
    resetCreationState 
  } = useSubscriptionCreation();

  // Clean up timeouts when component unmounts
  useEffect(() => {
    // Only run refresh on initial mount, not on every render
    if (initialMountRef.current) {
      initialMountRef.current = false;
      
      // No need to call refresh here since useSubscriptionStatus does
      // the initial fetch when user is available
    }
    
    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      // Reset states on unmount
      subscribingRef.current = false;
    };
  }, []);

  const handleSubscribe = useCallback(async () => {
    // Prevent multiple subscription attempts
    if (subscribingRef.current) return;
    
    try {
      subscribingRef.current = true;
      
      // Clean up any previous refresh timeouts
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      
      // Call the subscription function
      await _handleSubscribe(isSubscribed);
      
      // Schedule a refresh only if the component is still mounted
      refreshTimeoutRef.current = window.setTimeout(() => {
        // Only refresh if we're still on the pricing page
        // (the user might have been redirected to PayPal)
        if (window.location.pathname.includes('pricing')) {
          refreshSubscription(true);
          subscribingRef.current = false;
        }
      }, 2000);
    } catch (err) {
      console.error('Error in handleSubscribe:', err);
      subscribingRef.current = false;
    }
  }, [_handleSubscribe, isSubscribed, refreshSubscription]);

  // Combined loading state
  const isLoading = statusLoading || creationLoading || subscribingRef.current;

  // Clean up function for when leaving the pricing page
  const cleanup = useCallback(() => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    subscribingRef.current = false;
    resetCreationState();
    cleanupSubscriptionStatus();
  }, [resetCreationState, cleanupSubscriptionStatus]);

  // Clean up when component unmounts
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    isLoading,
    error,
    paypalError,
    hasCredentialError,
    handleSubscribe,
    isSubscribed,
    currentSubscription,
    refreshSubscription,
    cleanup
  };
};
